import "./ProductOrders.scss";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import GreenButton from "../../components/GreenButton/GreenButton";
import Download from "./../../assets/download-excel.svg";
import SearchIcon from "@mui/icons-material/Search";
import ShowUser from "./../../assets/show-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import MessageModals from "../../components/MessageModals/MessageModals";
import ColorButton from "../../components/ColorButton/ColorButton";
import api from "../../utils/api";
import dayjs from "dayjs";
import usePermissions from "../../hooks/usePermissions";
import { DatePicker, message } from "antd";
import { DataGrid } from "@mui/x-data-grid";
import { ProductOrdersColumns } from "../../SourceData";
import { Link } from "react-router-dom";
const { RangePicker } = DatePicker;

const ProductOrders = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");

  // State for filters
  const [query, setQuery] = useState("");
  const [view, setView] = useState("admin");
  const [openDelete, setOpenDelete] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [statusId, setStatusId] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adminFlag, setAdminFlag] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // New states for cancel order functionality
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  // Fetch countries data
  useEffect(() => {
    const getCountries = async () => {
      try {
        const res = await api.get("/countries");
        setCountries(res.data.data);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    getCountries();
  }, []);

  // Fetch orders based on filters
  useEffect(() => {
    fetchOrders();
  }, [view, countryId, statusId, adminFlag, query, dateRange]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = {
        "country_id[]": countryId || undefined, // Changed to use array notation in the key
        status_id: statusId || undefined,
        type: view === "factories" ? "factory" : "shop",
        flag: adminFlag ? "admin" : undefined,
        q: query || undefined,
        date_from: dateRange[0]
          ? dayjs(dateRange[0]).format("YYYY-MM-DD")
          : undefined,
        date_to: dateRange[1]
          ? dayjs(dateRange[1]).format("YYYY-MM-DD")
          : undefined,
      };

      // Remove undefined values
      Object.keys(params).forEach(
        (key) => params[key] === undefined && delete params[key]
      );

      const response = await api.get("/orders", { params });
      setOrders(response.data.data || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel order
  const handleOpenCancelOrder = (product) => {
    setSelectedProduct(product);
    setOpenCancelModal(true);
  };

  const handleCloseCancelOrder = () => {
    setOpenCancelModal(false);
    if (cancelSuccess) {
      setCancelSuccess(false);
      fetchOrders();
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedProduct) return;

    setCancelLoading(true);
    try {
      // Make API call to cancel the order
      await api.post(`/cancelOrder`, {
        id: selectedProduct.id,
        vendor_id: selectedProduct.vendor_id,
      });

      setCancelSuccess(true);
      message.success("تم إلغاء الطلب بنجاح");
    } catch (error) {
      console.error("Error canceling order:", error);
      message.error("حدث خطأ أثناء إلغاء الطلب");
    } finally {
      setCancelLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    fetchOrders();
  };

  const handleOpenDelete = (product) => {
    setSelectedProduct(product);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedProduct(null);
  };

  // Action column for the data grid
  const actionColumn = [
    {
      field: "action",
      headerName: "",
      width: 300,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            {hasPermission(69) && (
              <ColorButton
                size="large"
                color="#E63050"
                text={
                  params.row.status === "canceled"
                    ? "تم الغاء الطلب"
                    : "إلغاء الطلب"
                }
                onClick={() => handleOpenCancelOrder(params.row)}
                disabled={params.row.status === "canceled"}
              />
            )}

            {hasPermission(141) && (
              <Link
                className="cell-btn"
                to={`/product-orders/${params.row.id}`}
              >
                <img src={ShowUser} alt="" />
              </Link>
            )}
            {hasPermission(140) && (
              <div
                className="cell-btn"
                onClick={() => handleOpenDelete(params.row)}
              >
                <img src={DeleteUser} alt="" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="products">
      <div className="special-header">
        <p className="title">الطلبات على المنتجات</p>

        <div className="btns-container">
          <GreenButton
            text="طلبات المتاجر / منتجات الادمن"
            size={isMobile ? "full" : "fit"}
            variant={view === "admin" ? "fill" : "outlined"}
            onClick={() => setView("admin")}
          />

          <GreenButton
            text="طلبات المصانع"
            size={isMobile ? "full" : "medium"}
            variant={view === "factories" ? "fill" : "outlined"}
            onClick={() => setView("factories")}
          />
        </div>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="بحث باسم المزود او رقم الطلب"
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                width: isMobile
                  ? "100%"
                  : isTablet || view === "permissions"
                  ? "300px"
                  : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      onClick={handleSearch}
                      style={{ cursor: "pointer" }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <RangePicker
              format={"YYYY-MM-DD"}
              style={{
                width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                padding: "0px 10px",
                direction: "ltr",
              }}
              placeholder={["من", "الي"]}
              className="range-picker"
              value={dateRange}
              onChange={(dates) => {
                setDateRange(dates);
                fetchOrders();
              }}
              allowClear={true}
              onCalendarChange={(dates) => {
                if (!dates || dates.length === 0) {
                  setDateRange([null, null]);
                  fetchOrders();
                }
              }}
            />
          </div>
        </div>

        <div className="filters-container">
          <div className="filters">
            <FormControl
              sx={{
                width: isMobile ? "100%" : "160px",
              }}
            >
              <InputLabel
                htmlFor="country-select"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                نطاقات العمل
              </InputLabel>
              <Select
                labelId="country-select-label"
                id="country-select"
                displayEmpty={true}
                sx={{ borderRadius: "8px" }}
                value={countryId}
                onChange={(e) => setCountryId(e.target.value)}
              >
                <MenuItem value="">الجميع</MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name_ar}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              sx={{
                width: isMobile ? "100%" : "160px",
              }}
            >
              <InputLabel
                htmlFor="status-select"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                حالة الطلب
              </InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                displayEmpty={true}
                sx={{ borderRadius: "8px" }}
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
              >
                <MenuItem value="">الجميع</MenuItem>
                <MenuItem value="1">انتظار الموافقة</MenuItem>
                <MenuItem value="2">جاري التجهيز</MenuItem>
                <MenuItem value="3">جاري التوصيل</MenuItem>
                <MenuItem value="4">تم التسليم</MenuItem>
                <MenuItem value="5">الطلبات الملغاة</MenuItem>
                <MenuItem value="6">الطلبات المنتهية</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className="excels-actions">
            <img
              src={Download}
              alt="تحميل كملف اكسل"
              style={{ cursor: "pointer" }}
              onClick={() => {
                // TODO: Implement Excel download functionality
              }}
            />
          </div>
        </div>

        <div className="checked-container">
          <div className="item">
            <input
              type="checkbox"
              checked={adminFlag}
              onChange={(e) => setAdminFlag(e.target.checked)}
            />
            <label>منتجات الادمن</label>
          </div>
        </div>
      </div>
      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={ProductOrdersColumns.concat(actionColumn)}
          rows={orders.map((order, index) => ({
            ...order,
            index: index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          getRowId={(row) => row.id || row.index}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
          }}
        />
      </div>

      {/* Delete Order Modal */}
      <MessageModals
        open={openDelete}
        currentView="delete"
        route={`/orders/${selectedProduct?.id}`}
        onClose={handleCloseDelete}
        onDataChange={fetchOrders}
        text={"تم حذف الطلب بنجاح"}
      />

      {/* Cancel Order Modal */}
      <MessageModals
        open={openCancelModal}
        currentView={cancelSuccess ? "success" : "confirm"}
        title={cancelSuccess ? "نجاح" : "تأكيد إلغاء الطلب"}
        text={
          cancelSuccess
            ? "تم إلغاء الطلب بنجاح"
            : "هل أنت متأكد من إلغاء هذا الطلب بالكامل؟"
        }
        confirmText="إلغاء الطلب"
        cancelText="العودة"
        loading={cancelLoading}
        onConfirm={handleCancelOrder}
        onClose={handleCloseCancelOrder}
        onDataChange={fetchOrders}
      />
    </div>
  );
};

export default ProductOrders;
